import { useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { InteractablePopover } from 'ui-components/components/interactable-popover';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useSystemAutomation } from '../../../../../../../../contexts/system-automation-context';
import { SYSTEM_RULE_NAMES } from '../../../../../../../../contexts/system-automation-context/constants';
import { useHasParcelGuardAutomationRule } from '../../../../../../../../hooks/use-has-parcel-guard-automation-rule';
import { getAutomationsSettingsPath } from '../../../../../../../../routes/constants';
import { useWalletedInsuranceProviderId } from '../../../../../../../../services/insurance-service';
import { saveNewSellerSettings } from '../../../../../../../../store/seller/seller.actions';
import {
  useInsurancePreference,
  INSURANCE_PREFERENCE,
  saveInsurancePreference,
  type InsurancePreference,
} from '../../../../../../../../hooks/use-insurance-preference';
import { LogService } from '../../../../../../../../services/log-service';
import { Segment } from '../../../../../../../../services/analytics-services/segment';
import {
  INSURANCE_PREFERENCE_SELECTION_CHANGE,
  INSURANCE_OPTION_CLICKED,
  INSURANCE_POPOVER_VIEWED,
  FTU_INSURANCE_RULE_CREATED,
} from '../../../../../../../../constants/analytics-events';
import { postFilterSet, updateFilterSet } from '../../../../../../../../store/saved-filters-api';
import { addRule, updateRule } from '../../../../../../../../pages/settings/api/automation-rules';
import { ACTION_TYPE_INSURE_PACKAGE } from '../../../../../../../../pages/settings/automation-rules/constants/action-types';
import { generateNewAutomationRule } from '../../../../../../../../pages/settings/automation-rules/services';
import { FTU_WIZARD_PAGE } from '../../../../constants';
import { RateShopperOption } from '../../rate-shopper-option';
import messages from '../../messages';
import style from '../../style.module.less';
import { InsuranceRuleBuilder } from './components/insurance-rule-builder/insurance-rule-builder';
import { OPERATOR_MAP, ORDER_TOTAL_FILTER_COLUMN } from './constants';

import type { InsuranceRule } from './components/insurance-rule-builder/insurance-rule-builder';

const FTU_WIZARD_INSURANCE_PREFERENCES = 'ftu_wizard_insurance_preferences';
const PARCEL_GUARD_TERMS_URL = 'https://www.parcelguard.com/terms/';

const OPTION_CLICKED_LABELS: Record<string, string> = {
  [INSURANCE_PREFERENCE.INSURE_ALL]: 'All',
  [INSURANCE_PREFERENCE.INSURE_BY_RULES]: 'Rule',
  [INSURANCE_PREFERENCE.NONE]: 'None',
};

type InsurancePreferenceSelectionListProps = {
  onSelectionChange?: (selection: InsurancePreference) => void;
};

export const InsurancePreferenceSelectionList = ({ onSelectionChange }: InsurancePreferenceSelectionListProps) => {
  const dispatch = useDispatch();
  const insuranceProviderId = useWalletedInsuranceProviderId();
  const { getSystemRule, refreshAutomationRules } = useSystemAutomation();
  const hasParcelGuardAutomationRule = useHasParcelGuardAutomationRule();
  const { insurancePreference, isLoading, hasActiveInsuranceRule, hasExplicitNonePreference } = useInsurancePreference();
  const isInitializedRef = useRef(false);
  // Start unselected: a NONE preference should not pre-check any radio — force an explicit choice.
  const [selectedOption, setSelectedOption] = useState<InsurancePreference | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRuleSaved, setIsRuleSaved] = useState(false);
  const [savedRule, setSavedRule] = useState<InsuranceRule | null>(null);

  if (!isLoading && !isInitializedRef.current) {
    isInitializedRef.current = true;

    if (insurancePreference !== INSURANCE_PREFERENCE.NONE) {
      setSelectedOption(insurancePreference);
    }
    if (hasActiveInsuranceRule) setIsRuleSaved(true);
    queueMicrotask(() => onSelectionChange?.(insurancePreference));
  }

  const shouldShowNoCoverageBanner =
    (selectedOption === null && !hasExplicitNonePreference) || selectedOption === INSURANCE_PREFERENCE.NONE;

  const handleRuleSave = useCallback(
    async (rule: InsuranceRule) => {
      const ruleName = SYSTEM_RULE_NAMES.INSURE_BY_RULES;
      const existingRule = getSystemRule(ruleName);
      const filterOperator = OPERATOR_MAP[rule.operator];
      if (!filterOperator) {
        LogService.logError(new Error('Unknown insurance rule operator'), { operator: rule.operator });
        return;
      }

      setIsSaving(true);
      try {
        if (existingRule) {
          await updateFilterSet({
            orderFilterSetId: existingRule.filterId,
            name: ruleName,
            active: false,
            appliedFilters: {
              numericFilters: [
                {
                  column: ORDER_TOTAL_FILTER_COLUMN,
                  operator: filterOperator,
                  value: rule.value,
                  workingFilterId: GuidService.newGuid(),
                },
              ],
            },
          });
          await updateRule({ ...existingRule, active: true });
        } else {
          const filterResponse = await postFilterSet({
            name: ruleName,
            active: false,
            appliedFilters: {
              numericFilters: [
                {
                  column: ORDER_TOTAL_FILTER_COLUMN,
                  operator: filterOperator,
                  value: rule.value,
                  workingFilterId: GuidService.newGuid(),
                },
              ],
            },
          });
          const newRule = generateNewAutomationRule({
            name: ruleName,
            filterId: filterResponse.orderFilterSetId,
            orderRuleActions: [
              {
                actionTypeId: ACTION_TYPE_INSURE_PACKAGE,
                intParameter: insuranceProviderId,
              },
            ],
          });
          await addRule(newRule);
        }
        await refreshAutomationRules();
        setSavedRule(rule);
        setIsRuleSaved(true);
        Segment.trackEvent(FTU_INSURANCE_RULE_CREATED, {
          page: FTU_WIZARD_PAGE,
        });
      } catch (error) {
        LogService.logError(new Error('Failed to save insurance automation rule', { cause: error }));
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.insuranceRuleSaveError} />,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [insuranceProviderId, getSystemRule, refreshAutomationRules],
  );

  const handleRuleEdit = useCallback(() => {
    setIsRuleSaved(false);
  }, []);

  const handleOptionSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const previousSelection = selectedOption;
      const newSelection = e.target.value as InsurancePreference;
      setSelectedOption(newSelection);
      onSelectionChange?.(newSelection);

      Segment.trackEvent(INSURANCE_PREFERENCE_SELECTION_CHANGE, {
        selection: newSelection,
        source: FTU_WIZARD_INSURANCE_PREFERENCES,
      });

      Segment.trackEvent(INSURANCE_OPTION_CLICKED, {
        page: FTU_WIZARD_PAGE,
        optionClicked: OPTION_CLICKED_LABELS[newSelection],
      });

      // NONE is local-only in the FTU: do not persist, so pre-existing seller state is preserved.
      if (newSelection === INSURANCE_PREFERENCE.NONE) {
        return;
      }
      // The early return below guarantees this callback only runs when we have a provider, but narrow here so the save call is type-safe.
      if (!insuranceProviderId) {
        return;
      }

      try {
        const existingSystemRule = getSystemRule(SYSTEM_RULE_NAMES.INSURE_BY_RULES);
        const newSettings = await saveInsurancePreference({
          preference: newSelection,
          insuranceProviderId,
          existingSystemRule,
          refreshAutomationRules,
        });
        dispatch(saveNewSellerSettings(newSettings));
      } catch (error) {
        LogService.logError(new Error('Failed to update insurance preference', { cause: error }));
        setSelectedOption(previousSelection);
      }
    },
    [dispatch, selectedOption, insuranceProviderId, getSystemRule, refreshAutomationRules, onSelectionChange],
  );

  const handlePopoverOpen = useCallback(() => {
    Segment.trackEvent(INSURANCE_POPOVER_VIEWED, {
      page: FTU_WIZARD_PAGE,
    });
  }, []);

  if (!insuranceProviderId) {
    return null;
  }

  if (isLoading) {
    return (
      <FlexColumn className={style.section}>
        <Text textType={TextType.H3} value={messages.insurancePreferencesTitle} />
        <LoadingIndicator className={style.loading} />
      </FlexColumn>
    );
  }

  return (
    <FlexColumn className={style.section}>
      <FlexRow gap={Gap.Space8} style={{ alignItems: 'center' }}>
        <Text textType={TextType.H3} value={messages.insurancePreferencesTitle} />
        <InteractablePopover
          ignoreTooltip
          onPopOverOpen={handlePopoverOpen}
          popoverBody={
            <FlexColumn gap={Gap.Space8}>
              <Text textType={TextType.Paragraph} value={messages.insurancePopoverBody} />
              <Button href={PARCEL_GUARD_TERMS_URL} theme={THEME_CONSTANTS.link}>
                <FormattedMessage {...messages.insurancePopoverLearnMore} />
              </Button>
            </FlexColumn>
          }
          preferPlace='bottom'
          title={<FormattedMessage {...messages.insurancePopoverTitle} />}
        >
          <FontAwesomeIcon icon={faCircleInfo} size='sm' />
        </InteractablePopover>
      </FlexRow>
      {hasParcelGuardAutomationRule && (
        <BannerMessageV2
          className={style['active-parcel-guard-rules-banner']}
          content={
            <Text
              formattedMessageValues={{
                a: (chunks: React.ReactNode) => <Link to={getAutomationsSettingsPath()}>{chunks}</Link>,
              }}
              textType={TextType.Paragraph}
              value={messages.activeParcelGuardRulesBannerBody}
            />
          }
          header={<Text textType={TextType.Label} value={messages.activeParcelGuardRulesBannerTitle} />}
          layout={BannerLayout.Full}
          status={BannerStatus.Info}
        />
      )}
      <RadioCheckList className={style['rate-shopper-selection-list']}>
        <RateShopperOption
          defaultBadgeText={messages.recommendedBadge}
          description={messages.insureAllDescription}
          id={INSURANCE_PREFERENCE.INSURE_ALL}
          isSelected={selectedOption === INSURANCE_PREFERENCE.INSURE_ALL}
          onSelect={handleOptionSelect}
          showDefaultBadge
          title={messages.insureAllTitle}
          value={INSURANCE_PREFERENCE.INSURE_ALL}
        />
        {!hasParcelGuardAutomationRule && (
          <RateShopperOption
            description={messages.insureRulesDescription}
            id={INSURANCE_PREFERENCE.INSURE_BY_RULES}
            isSelected={selectedOption === INSURANCE_PREFERENCE.INSURE_BY_RULES}
            onSelect={handleOptionSelect}
            title={messages.insureRulesTitle}
            value={INSURANCE_PREFERENCE.INSURE_BY_RULES}
          >
            <InsuranceRuleBuilder
              isSaved={isRuleSaved}
              isSaving={isSaving}
              onEdit={handleRuleEdit}
              onSave={handleRuleSave}
              savedRule={savedRule}
            />
          </RateShopperOption>
        )}
        <RateShopperOption
          description={messages.insureSkipDescription}
          id={INSURANCE_PREFERENCE.NONE}
          isSelected={selectedOption === INSURANCE_PREFERENCE.NONE}
          onSelect={handleOptionSelect}
          title={messages.insureSkipTitle}
          value={INSURANCE_PREFERENCE.NONE}
        />
      </RadioCheckList>
      {shouldShowNoCoverageBanner && (
        <BannerMessageV2
          className={style['skip-warning']}
          content={<Text textType={TextType.Paragraph} value={messages.insureSkipWarning} />}
          layout={BannerLayout.Full}
          status={BannerStatus.Warning}
        />
      )}
    </FlexColumn>
  );
};
